import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  buildCalendarCells,
  isSameDay,
  MONTHS,
  toDateValue,
  WEEKDAYS,
} from "./datePickerUtils";

export default function CalendarGrid({
  viewYear,
  viewMonth,
  selectedDate,
  onSelectDate,
  onViewMonthChange,
  compact = false,
  showYearMonthSelect = false,
  minYear,
  maxYear,
  isDateDisabled,
  /** Map of "YYYY-MM-DD" → { type: "holiday"|"leave"|"weekoff", label?: string, color?: string } */
  dayMarkers,
}) {
  const today = new Date();
  const cells = buildCalendarCells(viewYear, viewMonth);
  const yearStart = minYear ?? today.getFullYear() - 100;
  const yearEnd = maxYear ?? today.getFullYear() + 10;
  const years = [];
  for (let y = yearEnd; y >= yearStart; y -= 1) years.push(y);

  const shiftMonth = (delta) => {
    const next = new Date(viewYear, viewMonth + delta, 1);
    onViewMonthChange(next.getFullYear(), next.getMonth());
  };

  const markerDotColor = (type) => {
    if (type === "holiday") return "#2563eb";
    if (type === "leave") return "#ef4444";
    if (type === "weekoff") return "#8b5cf6";
    return "#94a3b8";
  };

  return (
    <div className={compact ? "w-full" : "w-[300px]"}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 shrink-0"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {showYearMonthSelect ? (
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <select
              className="flex-1 min-w-0 text-sm font-medium border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
              value={viewMonth}
              onChange={(e) => onViewMonthChange(viewYear, Number(e.target.value))}
              aria-label="Month"
            >
              {MONTHS.map((name, idx) => (
                <option key={name} value={idx}>
                  {name}
                </option>
              ))}
            </select>
            <select
              className="w-[5.5rem] shrink-0 text-sm font-medium border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
              value={viewYear}
              onChange={(e) => onViewMonthChange(Number(e.target.value), viewMonth)}
              aria-label="Year"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="text-sm font-semibold text-slate-800 text-center flex-1">
            {MONTHS[viewMonth]} {viewYear}
          </p>
        )}

        <button
          type="button"
          onClick={() => shiftMonth(1)}
          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 shrink-0"
          aria-label="Next month"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((d) => (
          <div key={d} className="text-center text-[11px] font-medium text-slate-400 py-1">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map(({ date, inMonth }) => {
          const key = toDateValue(date);
          const marker = dayMarkers?.[key];
          const selected = isSameDay(date, selectedDate);
          const isToday = isSameDay(date, today);
          const disabled = isDateDisabled?.(date);

          let bgClass = "";
          if (!selected && !disabled && inMonth && marker) {
            if (marker.type === "weekoff") bgClass = "bg-violet-50";
            else if (marker.type === "holiday") bgClass = "bg-blue-50";
            else if (marker.type === "leave") bgClass = "bg-red-50";
          }

          return (
            <div key={key} className="relative flex flex-col items-center">
              <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && onSelectDate(date)}
                title={marker?.label || undefined}
                className={[
                  "h-9 w-9 mx-auto rounded-full text-sm font-medium transition",
                  disabled ? "text-slate-200 cursor-not-allowed" : "",
                  !disabled && !inMonth ? "text-slate-300" : "",
                  !disabled && inMonth && !selected ? `text-slate-700 hover:bg-teal-50 ${bgClass}` : "",
                  selected ? "bg-teal-600 text-white hover:bg-teal-600" : "",
                  !selected && !disabled && isToday ? "ring-2 ring-teal-200 ring-inset" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {date.getDate()}
              </button>
              {marker && inMonth && (
                <span
                  className="absolute bottom-0 w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: markerDotColor(marker.type) }}
                />
              )}
            </div>
          );
        })}
      </div>

      {dayMarkers && (
        <div className="flex items-center gap-3 mt-3 pt-2 border-t border-slate-100 flex-wrap">
          {[
            { type: "holiday", label: "Holiday" },
            { type: "leave", label: "Leave" },
            { type: "weekoff", label: "Week Off" },
          ].map(({ type, label }) => (
            <span key={type} className="flex items-center gap-1 text-[10px] text-slate-500">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: markerDotColor(type) }}
              />
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
